import React, { ChangeEvent, ForwardedRef, forwardRef, useState } from 'react';
import { cn } from 'fc/lib/utils';

interface FileUploadProps {
  onChange: (file: File | null) => void;
  error?: React.ReactNode | string;
  defaultFileName?: string | null;
  accept: string; // Accept file type (e.g., .png, .jpg for image or .pdf for PDF)
  maxFileSize?: number; // Optional: maximum file size in bytes
}

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  (
    { onChange, error, defaultFileName, accept, maxFileSize = 5 * 1024 * 1024 },
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const [localError, setLocalError] = useState<string | null>(null);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0] || null;

      if (selectedFile) {
        const validFileTypes = accept.split(',').map((type) => type.trim());
        if (!validFileTypes.includes(selectedFile.type)) {
          setLocalError(`Only ${accept} files are allowed.`);
          onChange(null);
          return;
        }

        if (selectedFile.size > maxFileSize) {
          setLocalError(
            `File size must not exceed ${maxFileSize / (1024 * 1024)} MB.`
          );
          onChange(null);
          return;
        }

        setLocalError(null);
        onChange(selectedFile);
      } else {
        setLocalError(null);
        onChange(null);
      }
    };

    return (
      <div className="flex flex-col gap-2">
        <div className="relative">
          <input
            ref={ref}
            type="file"
            accept={accept} // Dynamically set the accept attribute
            onChange={handleFileChange}
            className={cn(
              'absolute inset-0 opacity-0 w-full h-full cursor-pointer'
            )}
          />

          <div className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
            <span className="text-sm font-normal text-blue-600 text-primary">
              Choose File
            </span>
            <span className="text-sm mx-auto font-normal text-gray-600">
              {defaultFileName || 'No file chosen'}
            </span>
          </div>
        </div>
        {error || localError ? (
          <p className="text-sm text-red-500">{error || localError}</p>
        ) : null}
      </div>
    );
  }
);

FileUpload.displayName = 'FileUpload';
